import React, { useEffect, useMemo } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  createServiceRecord,
  fetchCustomerAgreements,
  fetchReamingRights,
} from "../../slices/apps/agreementSlice";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// react select
import Select from "react-select";

//Import Flatepicker
import Flatpickr from "react-flatpickr";
import { Turkish } from "flatpickr/dist/l10n/tr.js";

import { serviceKeys } from "../../common/data/serviceKeys";

const toLocalISOString = (inputDate) => {
  const date = new Date(inputDate);
  date.setHours(0, 0, 0, 0);
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString();
};

const ServiceRecordAddModal = ({ modal, setModal }) => {
  const dispatch = useDispatch();
  const initialDate = useMemo(() => toLocalISOString(new Date()), [modal]);

  const {
    agreementCustomers,
    customerAgreements,
    customerAgreementsLoading,
    reamingRights,
    reamingRightsLoading,
    createServiceRecordLoading,
  } = useSelector((state) => {
    return {
      agreementCustomers: state.agreement.agreementCustomers,
      customerAgreements: state.agreement.customerAgreements,
      customerAgreementsLoading: state.agreement.customerAgreementsLoading,
      reamingRights: state.agreement.reamingRights,
      reamingRightsLoading: state.agreement.reamingRightsLoading,
      createServiceRecordLoading: state.agreement.createServiceRecordLoading,
    };
  }, shallowEqual);

  useEffect(() => {
    if (modal) {
      validation.resetForm();
    }
  }, [modal]);

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      customerId: "",
      aggrementNo: "",
      service: "",
      date: initialDate,
      description: "",
      serviceAmount: 0,
    },

    validationSchema: Yup.object({
      customerId: Yup.string().required("Lütfen müşteri seçin"),
      aggrementNo: Yup.string().required("Lütfen sözleşme seçin"),
      service: Yup.string().required("Lütfen hizmet seçin"),
      serviceAmount: Yup.number()
        .typeError("Lütfen geçerli bir sayı girin")
        .positive("Fiyat 0'dan büyük olmalıdır")
        .required("Birim fiyat alanı zorunludur"),
    }),

    onSubmit: async (values) => {
      const serviceModel = {};

      serviceKeys.forEach(({ key }) => {
        serviceModel[key] = key === values.service ? 1 : 0;
      });

      const finalData = {
        CustomerId: values.customerId,
        AggrementNo: values.aggrementNo,
        Description: values.description,
        Date: toLocalISOString(values.date || new Date()),
        Description: values.description,
        ServiceAmount: parseFloat(values.serviceAmount),
        ...serviceModel,
      };

      const resultAction = await dispatch(createServiceRecord(finalData));

      if (resultAction.meta.requestStatus === "fulfilled") {
        setModal(false);
      }
    },
  });

  const blockInvalidChars = (e) => {
    if (["-", "e", "+", "E"].includes(e.key)) e.preventDefault();
  };

  return (
    <Modal
      size="lg"
      isOpen={modal}
      toggle={() => {
        setModal(false);
      }}
    >
      <ModalHeader
        className="modal-title "
        id="myLargeModalLabel"
        toggle={() => {
          setModal(false);
        }}
      >
        Yeni Hizmet Kaydı Ekle
      </ModalHeader>

      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
          action="#"
          autoComplete="off"
        >
          <Row className="align-items-start gy-3">
            <Col lg={12}>
              <Label>Müşteri</Label>
              <Select
                placeholder="Müşteri Seçiniz"
                name="customerId"
                isClearable
                noOptionsMessage={() => "Seçenek bulunamadı"}
                options={agreementCustomers}
                value={
                  agreementCustomers.find(
                    (item) => item.value === validation.values.customerId
                  ) || ""
                }
                onChange={(value) => {
                  const customerId = value?.value || "";

                  validation.setFieldValue("customerId", customerId);

                  if (customerId) {
                    dispatch(fetchCustomerAgreements(customerId));
                  } else {
                    validation.setFieldValue("aggrementNo", "");
                    validation.setFieldValue("service", "");
                  }
                }}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor:
                      validation.touched.customerId &&
                      validation.errors.customerId &&
                      !state.isFocused
                        ? "#e30a17"
                        : "#e9ebec",
                  }),
                }}
              />
              {validation.touched.customerId &&
                validation.errors.customerId && (
                  <div className="d-block invalid-feedback">
                    {validation.errors.customerId}
                  </div>
                )}
            </Col>

            <Col lg={6}>
              <Label>Sözleşme</Label>
              <Select
                placeholder="Sözleşme Seçiniz"
                name="aggrementNo"
                isClearable
                noOptionsMessage={() => "Seçenek bulunamadı"}
                isDisabled={
                  !validation.values.customerId || customerAgreementsLoading
                }
                options={customerAgreements}
                value={
                  customerAgreements.find(
                    (item) => item.value === validation.values.aggrementNo
                  ) || ""
                }
                onChange={(value) => {
                  const aggrementNo = value?.value || "";

                  validation.setFieldValue("aggrementNo", aggrementNo);

                  if (aggrementNo) {
                    dispatch(fetchReamingRights(value.value));
                  } else {
                    validation.setFieldValue("service", "");
                  }
                }}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor:
                      validation.touched.aggrementNo &&
                      validation.errors.aggrementNo &&
                      !state.isFocused
                        ? "#e30a17"
                        : "#e9ebec",
                  }),
                }}
              />
              {validation.touched.aggrementNo &&
                validation.errors.aggrementNo && (
                  <div className="d-block invalid-feedback">
                    {validation.errors.aggrementNo}
                  </div>
                )}
            </Col>

            <Col lg={6}>
              <Label>Hizmet</Label>
              <Select
                placeholder="Hizmet Seçiniz"
                name="service"
                isClearable
                noOptionsMessage={() => "Seçenek bulunamadı"}
                isDisabled={
                  !validation.values.aggrementNo || reamingRightsLoading
                }
                options={reamingRights}
                isOptionDisabled={(option) => option.count === 0}
                value={
                  reamingRights.find(
                    (item) => item.value === validation.values.service
                  ) || ""
                }
                onChange={(value) => {
                  validation.setFieldValue("service", value ? value.value : "");
                }}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor:
                      validation.touched.service &&
                      validation.errors.service &&
                      !state.isFocused
                        ? "#e30a17"
                        : "#e9ebec",
                  }),
                }}
              />
              {validation.touched.service && validation.errors.service && (
                <div className="d-block invalid-feedback">
                  {validation.errors.service}
                </div>
              )}
            </Col>

            <Col lg={6}>
              <Label>Tarih</Label>
              <Flatpickr
                className={
                  validation.touched.date && validation.errors.date
                    ? "form-control is-invalid"
                    : "form-control"
                }
                options={{
                  dateFormat: "d.m.Y",
                  locale: { ...Turkish },
                }}
                value={
                  validation.values.date
                    ? new Date(validation.values.date)
                    : null
                }
                onChange={(selectedDates) => {
                  if (selectedDates && selectedDates.length > 0) {
                    const date = new Date(selectedDates[0]);
                    validation.setFieldValue("date", date.toISOString());
                  }
                }}
              />
              {validation.touched.date && validation.errors.date && (
                <div type="invalid" className="d-block invalid-feedback">
                  {validation.errors.date}
                </div>
              )}
            </Col>

            <Col lg={6}>
              <Label htmlFor="serviceAmount">Tutar (₺)</Label>
              <Input
                id="serviceAmount"
                name="serviceAmount"
                type="number"
                placeholder="1500"
                value={validation.values.serviceAmount}
                onKeyDown={blockInvalidChars}
                onChange={validation.handleChange}
                invalid={
                  !!(
                    validation.touched.serviceAmount &&
                    validation.errors.serviceAmount
                  )
                }
              />
              {validation.touched.serviceAmount &&
              validation.errors.serviceAmount ? (
                <FormFeedback type="invalid">
                  {validation.errors.serviceAmount}
                </FormFeedback>
              ) : null}
            </Col>

            <Col lg={12}>
              <Label>Açıklama</Label>
              <Input
                name="description"
                className="form-control"
                placeholder="Açıklama..."
                type="textarea"
                autoComplete="off"
                rows="2"
                onChange={validation.handleChange}
                value={validation.values.description || ""}
                invalid={
                  !!(
                    validation.touched.description &&
                    validation.errors.description
                  )
                }
                style={{ resize: "none" }}
              />
            </Col>
          </Row>

          <div className="d-flex align-items-center gap-3 mt-4">
            <Button
              className="btn btn-sm ms-auto nexttab nexttab"
              color="primary"
              type="submit"
              disabled={createServiceRecordLoading}
            >
              Kaydet
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default ServiceRecordAddModal;
